import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Field } from "@/pages/admin/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, CheckCircle2, ArrowLeft, Play, Pause, XCircle, BellRing, Search, X, FileText, Trash2, AlertTriangle, RefreshCw } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const FYS = ["2023-24", "2024-25", "2025-26", "2026-27"];
const empty = { name_en: "", name_gu: "", explanation_en: "", series_code: "fees2026-27", fy: "2026-27", amount: 500, audience: { type: "all" }, due_date: "" };

const STATUS = {
  draft: { label: "Draft", cls: "bg-gmoaa-border-subtle text-gmoaa-text-dark" },
  active: { label: "Active", cls: "bg-gmoaa-green-light text-gmoaa-green" },
  paused: { label: "Paused", cls: "bg-gmoaa-saffron-light text-gmoaa-saffron-hover" },
  closed: { label: "Closed", cls: "bg-gmoaa-red-bg text-gmoaa-red" },
};

const StatusChip = ({ status }) => {
  const s = STATUS[status] || STATUS.draft;
  return <span data-testid={`status-${status}`} className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.cls}`}>{s.label}</span>;
};

export const CollectionsSection = () => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [active, setActive] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [memSearch, setMemSearch] = useState("");
  const [memResults, setMemResults] = useState([]);
  const [pickedMembers, setPickedMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sig, setSig] = useState(null);
  const [receipts, setReceipts] = useState([]);

  const load = () => adminApi.collections().then(setRows);
  useEffect(() => {
    load();
    adminApi.districts().then(setDistricts).catch(() => {});
    adminApi.receiptSignature().then(setSig).catch(() => {});
    adminApi.adminReceipts().then(setReceipts).catch(() => {});
  }, []);
  const regenPdf = async (r) => {
    try { const blob = await adminApi.regenerateReceiptPdf(r.id); window.open(URL.createObjectURL(blob), "_blank"); }
    catch { toast.error("Could not generate PDF"); }
  };
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const setFy = (fy) => setForm((f) => ({ ...f, fy, series_code: f.series_code.startsWith("fees") ? `fees${fy}` : f.series_code }));

  const setAudType = (type) => {
    setForm((f) => ({ ...f, audience: type === "all" ? { type: "all" } : type === "districts" ? { type: "districts", districts: [] } : { type: "members", members: [] } }));
    if (type !== "members") { setPickedMembers([]); setMemResults([]); setMemSearch(""); }
  };

  const toggleDistrict = (slug) => setForm((f) => {
    const cur = f.audience.districts || [];
    const next = cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug];
    return { ...f, audience: { type: "districts", districts: next } };
  });

  const searchMembers = async (q) => {
    setMemSearch(q);
    if (q.trim().length < 2) { setMemResults([]); return; }
    try { setMemResults(await adminApi.members({ q })); } catch { setMemResults([]); }
  };
  const addMember = (m) => {
    if (pickedMembers.find((p) => p.id === m.id)) return;
    const next = [...pickedMembers, m];
    setPickedMembers(next);
    setForm((f) => ({ ...f, audience: { type: "members", members: next.map((x) => x.id) } }));
  };
  const removeMember = (id) => {
    const next = pickedMembers.filter((p) => p.id !== id);
    setPickedMembers(next);
    setForm((f) => ({ ...f, audience: { type: "members", members: next.map((x) => x.id) } }));
  };

  const resetForm = () => { setForm(empty); setPickedMembers([]); setMemResults([]); setMemSearch(""); };

  const create = async () => {
    if (!form.name_en || !form.series_code) { toast.error("Fee head and series code required"); return; }
    if (form.audience.type === "districts" && !(form.audience.districts || []).length) { toast.error("Pick at least one district"); return; }
    if (form.audience.type === "members" && !(form.audience.members || []).length) { toast.error("Pick at least one member"); return; }
    try {
      const r = await adminApi.createCollection(form);
      toast.success(`Draft saved — ${r.audience_count} members in audience`);
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doAction = async (c, action) => {
    setBusy(true);
    try {
      if (action === "activate") { const r = await adminApi.activateCollection(c.id); toast.success(`Activated — shown to ${r.asked} members`); }
      if (action === "pause") { await adminApi.pauseCollection(c.id); toast.success("Paused — hidden from members"); }
      if (action === "close") { await adminApi.closeCollection(c.id); toast.success("Closed — no further payments"); }
      if (action === "delete") { await adminApi.deleteCollection(c.id); toast.success("Draft deleted"); if (active && active.id === c.id) setActive(null); }
      load();
      if (active && active.id === c.id) setActive({ ...active, status: action === "activate" ? "active" : action === "pause" ? "paused" : "closed" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const openColl = async (c) => { setActive(c); setReqs(await adminApi.collectionRequests(c.id)); };
  const record = async (r) => {
    try { const res = await adminApi.recordPayment(r.id, { method: "offline" }); toast.success(`Receipt ${res.receipt_number}`); openColl(active); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remind = async () => {
    try { const r = await adminApi.remindUnpaid(active.id); toast.success(`Reminder emailed to ${r.reminded} member(s)`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // ---- Detail view ----
  if (active) {
    const paid = reqs.filter((r) => r.status === "paid");
    const unpaid = reqs.filter((r) => r.status !== "paid");
    return (
      <div>
        <button onClick={() => setActive(null)} className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-gmoaa-green"><ArrowLeft size={15} /> Back to fee demands</button>
        <div className="mb-1 flex flex-wrap items-center gap-3">
          <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">{active.name_en}</h2>
          <StatusChip status={active.status} />
        </div>
        <p className="mb-4 text-sm text-gmoaa-text-muted">{active.fy ? `${active.fy} · ` : ""}{inr(active.amount)} · {paid.length}/{reqs.length} paid · collected {inr(paid.length * active.amount)}{active.due_date ? ` · last date ${active.due_date}` : ""}</p>

        <div className="mb-3 flex flex-wrap gap-2">
          {active.status !== "active" && active.status !== "closed" && <Button size="sm" data-testid="detail-activate" disabled={busy} onClick={() => doAction(active, "activate")} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover"><Play size={14} className="mr-1" /> Activate</Button>}
          {active.status === "active" && <Button size="sm" data-testid="detail-pause" disabled={busy} onClick={() => doAction(active, "pause")} className="bg-gmoaa-saffron text-white hover:bg-gmoaa-saffron-hover"><Pause size={14} className="mr-1" /> Pause / Hide</Button>}
          {active.status !== "closed" && <Button size="sm" variant="outline" data-testid="detail-close" disabled={busy} onClick={() => doAction(active, "close")} className="border-gmoaa-red text-gmoaa-red hover:bg-gmoaa-red-bg"><XCircle size={14} className="mr-1" /> Close</Button>}
          {unpaid.length > 0 && <Button size="sm" variant="outline" data-testid="remind-unpaid" onClick={remind} className="border-gmoaa-green text-gmoaa-green hover:bg-gmoaa-green-light"><BellRing size={14} className="mr-1" /> Remind unpaid members</Button>}
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div>
            <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gmoaa-green"><CheckCircle2 size={15} /> Paid ({paid.length})</h3>
            <div className="space-y-2" data-testid="paid-group">
              {paid.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-xl border border-gmoaa-border-subtle bg-white p-3">
                  <div><p className="font-medium text-gmoaa-text-dark">{r.member_name_en}</p><p className="text-xs text-gmoaa-text-muted">{r.district} · {inr(r.amount)} · {r.method}{r.receipt_number ? ` · ${r.receipt_number}` : ""}</p></div>
                  <FileText size={15} className="text-gmoaa-green" />
                </div>
              ))}
              {paid.length === 0 && <p className="text-sm text-gmoaa-text-muted">No payments yet.</p>}
            </div>
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold text-gmoaa-saffron-hover">Not yet paid ({unpaid.length})</h3>
            <div className="space-y-2" data-testid="unpaid-group">
              {unpaid.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-xl border border-gmoaa-border-subtle bg-white p-3">
                  <div><p className="font-medium text-gmoaa-text-dark">{r.member_name_en}</p><p className="text-xs text-gmoaa-text-muted">{r.district} · {inr(r.amount)}</p></div>
                  <Button size="sm" data-testid={`record-${r.id}`} onClick={() => record(r)} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Record payment</Button>
                </div>
              ))}
              {unpaid.length === 0 && <p className="text-sm text-gmoaa-text-muted">Everyone has paid.</p>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- List view ----
  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">Fee Demands</h2>
        <Button data-testid="collection-add-btn" onClick={() => { resetForm(); setOpen(true); }} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover"><Plus size={16} className="mr-1" /> New fee demand</Button>
      </div>
      {sig && !sig.uploaded && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-gmoaa-red/40 bg-gmoaa-red-bg p-3 text-sm font-medium text-gmoaa-red" data-testid="collections-signature-warning">
          <AlertTriangle size={16} /> Treasurer signature not uploaded — receipts print a blank signature line. Add it in Important Bar → Treasurer signature.
        </div>
      )}
      <div className="space-y-2">
        {rows.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-xl border border-gmoaa-border-subtle bg-white p-4">
            <button onClick={() => openColl(c)} className="flex-1 text-left">
              <div className="flex items-center gap-2"><p className="font-medium text-gmoaa-text-dark">{c.name_en}</p><StatusChip status={c.status || "active"} /></div>
              <p className="text-xs text-gmoaa-text-muted">{c.fy ? `${c.fy} · ` : ""}{c.series_code} · {inr(c.amount)} · {c.paid}/{c.sent} paid</p>
            </button>
            <div className="flex items-center gap-2">
              {(c.status === "draft" || c.status === "paused") && <Button size="sm" data-testid={`activate-${c.id}`} disabled={busy} onClick={() => doAction(c, "activate")} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover"><Play size={13} className="mr-1" /> Activate</Button>}
              {c.status === "active" && <Button size="sm" data-testid={`pause-${c.id}`} disabled={busy} onClick={() => doAction(c, "pause")} className="bg-gmoaa-saffron text-white hover:bg-gmoaa-saffron-hover"><Pause size={13} className="mr-1" /> Pause</Button>}
              {c.status !== "closed" && <Button size="sm" variant="outline" data-testid={`close-${c.id}`} disabled={busy} onClick={() => doAction(c, "close")} className="border-gmoaa-red text-gmoaa-red hover:bg-gmoaa-red-bg"><XCircle size={13} /></Button>}
              {c.status === "draft" && <Button size="sm" variant="ghost" data-testid={`delete-${c.id}`} disabled={busy} onClick={() => { if (window.confirm("Delete this draft demand?")) doAction(c, "delete"); }} className="text-gmoaa-red hover:bg-gmoaa-red-bg"><Trash2 size={13} /></Button>}
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-gmoaa-text-muted">No fee demands yet. Create one — it stays a draft until you activate it.</p>}
      </div>

      {receipts.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-3 font-serif text-lg font-semibold text-gmoaa-green">Issued receipts</h3>
          <div className="space-y-2" data-testid="admin-receipts-list">
            {receipts.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl border border-gmoaa-border-subtle bg-white p-3" data-testid={`receipt-row-${r.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gmoaa-text-dark">{r.number} · {r.member_name_en} {r.status === "cancelled" && <span className="text-gmoaa-red">(cancelled)</span>}</p>
                  <p className="truncate text-xs text-gmoaa-text-muted">{inr(r.amount)} · {r.purpose_en || r.series}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => regenPdf(r)} data-testid={`receipt-regen-${r.id}`} className="border-gmoaa-green text-gmoaa-green hover:bg-gmoaa-green-light"><RefreshCw size={13} className="mr-1" /> Regenerate PDF</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto bg-gmoaa-cream">
          <DialogHeader><DialogTitle className="text-gmoaa-green">New fee demand</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Field label="Fee head / cause (English)" value={form.name_en} onChange={set("name_en")} testid="coll-name" /></div>
            <div className="sm:col-span-2"><Field label="Fee head (Gujarati)" value={form.name_gu} onChange={set("name_gu")} /></div>
            <div>
              <p className="mb-1 text-xs font-medium text-gmoaa-text-muted">Year</p>
              <Select value={form.fy} onValueChange={setFy}>
                <SelectTrigger data-testid="coll-year" className="border-gmoaa-border-subtle bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{FYS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Field label="Amount (₹)" type="number" value={form.amount} onChange={set("amount")} testid="coll-amount" />
            <Field label="Last date (due)" type="date" value={form.due_date} onChange={set("due_date")} />
            <Field label="Series code (receipt)" value={form.series_code} onChange={set("series_code")} testid="coll-series" />

            <div className="sm:col-span-2">
              <p className="mb-1 text-xs font-medium text-gmoaa-text-muted">Who to ask</p>
              <Select value={form.audience.type} onValueChange={setAudType}>
                <SelectTrigger data-testid="coll-audience" className="border-gmoaa-border-subtle bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everyone (all members)</SelectItem>
                  <SelectItem value="districts">Selected districts</SelectItem>
                  <SelectItem value="members">Hand-picked members</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.audience.type === "districts" && (
              <div className="sm:col-span-2 max-h-48 overflow-y-auto rounded-lg border border-gmoaa-border-subtle bg-white p-3" data-testid="district-picker">
                <div className="grid grid-cols-2 gap-2">
                  {districts.map((d) => (
                    <label key={d.slug} className="flex items-center gap-2 text-sm text-gmoaa-text-dark">
                      <Checkbox checked={(form.audience.districts || []).includes(d.slug)} onCheckedChange={() => toggleDistrict(d.slug)} className="border-gmoaa-border-subtle data-[state=checked]:bg-gmoaa-green data-[state=checked]:border-gmoaa-green" />
                      {d.name_en}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {form.audience.type === "members" && (
              <div className="sm:col-span-2">
                <div className="relative">
                  <Search size={15} className="absolute left-3 top-2.5 text-gmoaa-text-muted" />
                  <Input data-testid="member-search" value={memSearch} onChange={(e) => searchMembers(e.target.value)} placeholder="Search members by name or ID…" className="border-gmoaa-border-subtle bg-white pl-9" />
                </div>
                {memResults.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-gmoaa-border-subtle bg-white">
                    {memResults.map((m) => (
                      <button key={m.id} type="button" onClick={() => addMember(m)} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gmoaa-green-light/50">
                        <span>{m.name_en} <span className="text-xs text-gmoaa-text-muted">· {m.member_id} · {m.district_slug}</span></span>
                        <Plus size={14} className="text-gmoaa-green" />
                      </button>
                    ))}
                  </div>
                )}
                {pickedMembers.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="picked-members">
                    {pickedMembers.map((m) => (
                      <span key={m.id} className="flex items-center gap-1 rounded-full bg-gmoaa-green-light px-2.5 py-1 text-xs text-gmoaa-green">
                        {m.name_en}<button type="button" onClick={() => removeMember(m.id)}><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter><Button data-testid="coll-save" onClick={create} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover"><Plus size={15} className="mr-1.5" /> Save as draft</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
